/**
 * DocumentMappingService - Manages the mapping between application sections and their required documents
 * 
 * This service provides a centralized way to define and access the relationship between
 * application sections and their associated document requirements.
 */

import { CommonUtils } from "../../(common-lib)/CommonUtils";

export enum DocumentStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  NEEDS_REVIEW = 'needs_review'
}

export interface DocumentRequirement {
  id: string;
  name: string;
  required: boolean;
  description: string;
}

export interface DocumentItem {
  id: string;
  name: string;
  required: boolean;
  description: string;

  uploadedIds: string[];

  documentId: string; 
  fileName: string;
  uploadedBy: string;
  uploadDate: string;
  fileSize: number;
  fileType: string;

  status: DocumentStatus;
  annotation: string;
  rejectionReason: string;
  title: string;
}

export interface SectionDocumentMapping {
  sectionId: string;
  sectionTitle: string;
  documents: DocumentItem[];
}

export class DocumentMappingService {
  private static instance: DocumentMappingService;
  private sectionDocumentMappings: Map<string, DocumentItem[]>;

  private constructor() {
    this.sectionDocumentMappings = new Map();
  }

  public static getInstance(): DocumentMappingService {
    if (!DocumentMappingService.instance) {
      DocumentMappingService.instance = new DocumentMappingService();
    }
    return DocumentMappingService.instance;
  }

  /**
   * Get all documents that have been uploaded
   * @returns Array of uploaded documents with their details
   */
  public getUploadedDocuments(): Array<{documentId: string; title: string; status: DocumentStatus; fileName: string}> {
    const uploadedDocs: Array<{documentId: string; title: string; status: DocumentStatus; fileName: string}> = [];
    
    this.sectionDocumentMappings.forEach((documents) => {
      documents.forEach(doc => {
        if (doc.status === DocumentStatus.COMPLETED || doc.status === DocumentStatus.NEEDS_REVIEW) {
          uploadedDocs.push({
            documentId: doc.documentId,
            title: doc.title,
            status: doc.status,
            fileName: doc.fileName || ''
          });
        }
      });
    });
    
    return uploadedDocs;
  }

  /**
   * Update the status and optional filename of a document
   * @param documentId The ID of the document to update
   * @param status The new status of the document
   * @param fileName Optional filename to update
   */
  public updateDocumentStatus(documentId: string, status: DocumentStatus, fileName?: string, uploadedId?: string): void {
    for (const [sectionId, documents] of this.sectionDocumentMappings.entries()) {
      const docIndex = documents.findIndex(doc => doc.documentId === documentId);
      if (docIndex >= 0) {
        const currentDoc = documents[docIndex];
        const updatedDoc = { ...currentDoc };
        
        // Update status if provided, otherwise determine based on context
        if (status) {
          updatedDoc.status = status;
        } else if (fileName) {
          updatedDoc.status = DocumentStatus.COMPLETED;
        } else if (!currentDoc.status || currentDoc.status === DocumentStatus.NOT_STARTED) {
          updatedDoc.status = DocumentStatus.NOT_STARTED;
        }
        
        if (fileName) {
          updatedDoc.fileName = fileName;
        }

        if (uploadedId) {
          updatedDoc.uploadedIds.push(uploadedId);
        }
        
        documents[docIndex] = updatedDoc;
        this.sectionDocumentMappings.set(sectionId, [...documents]);
        break;
      }
    }
  }

  /**
   * Get all document annotations across all sections
   * @returns Array of Annotation objects for documents with annotations
   */
  public getAllDocumentAnnotations(): Array<{
    id: string;
    documentId: string;
    type: 'document';
    message: string;
    severity: 'error' | 'warning' | 'info';
    createdAt: string;
    createdBy: string;
    resolved: boolean;
  }> {
    const annotations: any[] = [];
    const now = new Date().toISOString();
    
    this.sectionDocumentMappings.forEach((documents) => {
      documents.forEach(doc => {
        if (doc.annotation) {
          annotations.push({
            id: `doc_ann_${doc.documentId}`,
            documentId: doc.documentId,
            type: 'document' as const,
            message: doc.annotation,
            severity: 'error' as const, // Default to error severity
            createdAt: now,
            createdBy: 'System',
            resolved: !doc.annotation // Mark as resolved if no annotation message
          });
        }
      });
    });
    
    return annotations;
  }

  /**
   * Update or add an annotation for a document
   * @param documentId The ID of the document to annotate
   * @param message The annotation message
   */
  public updateDocumentAnnotation(
    documentId: string,
    message: string
  ): void {
    let found = false;
    
    this.sectionDocumentMappings.forEach((documents, sectionId) => {
      const docIndex = documents.findIndex(doc => doc.documentId === documentId);
      if (docIndex !== -1) {
        found = true;
        const currentDoc = documents[docIndex];
        
        // If there's a message, mark as NEEDS_REVIEW, otherwise keep current status or default to NOT_STARTED
        const newStatus = message 
          ? DocumentStatus.NEEDS_REVIEW 
          : (currentDoc.status || DocumentStatus.NOT_STARTED);
        
        const updatedDoc: DocumentItem = {
          ...currentDoc,
          annotation: message,
          status: newStatus
        };
        
        const updatedDocuments = [...documents];
        updatedDocuments[docIndex] = updatedDoc;
        this.sectionDocumentMappings.set(sectionId, updatedDocuments);
      }
    });
    
    if (!found) {
      CommonUtils.logWarn(`Document with ID ${documentId} not found for annotation`);
    }
  }

  /**
   * Clear an annotation for a document
   * @param documentId The ID of the document to clear annotations for
   */
  public clearDocumentAnnotation(documentId: string): void {
    this.sectionDocumentMappings.forEach((documents, sectionId) => {
      const docIndex = documents.findIndex(doc => doc.documentId === documentId);
      if (docIndex !== -1) {
        const currentDoc = documents[docIndex];
        const updatedDoc: DocumentItem = {
          ...currentDoc,
          annotation: '',
          status: currentDoc.fileName ? DocumentStatus.COMPLETED : DocumentStatus.NOT_STARTED
        };
        
        const updatedDocuments = [...documents];
        updatedDocuments[docIndex] = updatedDoc;
        this.sectionDocumentMappings.set(sectionId, updatedDocuments);
      }
    });
  }

  /**
   * Create a new document item with default values
   * @param documentId Unique identifier for the document
   * @param name Display name of the document
   * @param required Whether the document is required
   * @param description Description of the document
   * @param status Document status (defaults to 'pending')
   * @param fileName Name of the uploaded file (if any)
   * @param annotation Any annotation for the document (if any)
   */
  private createDocumentItem(
    documentId: string,
    name: string,
    required: boolean,
    description: string = ''
  ): DocumentItem {
    return {
      id: documentId,
      name,
      required,
      status: DocumentStatus.NOT_STARTED,
      description,
      annotation: '',
      fileName: '',
      uploadDate: '',
      fileSize: 0,
      fileType: '',
      rejectionReason: '',
      uploadedBy: '',
      title: name,
      documentId,
      uploadedIds: []
    };
  }

  /**
   * Update a document's status and annotation
   * @param sectionId The ID of the section containing the document
   * @param documentId The ID of the document to update
   * @param status The new status for the document
   * @param fileName Optional file name if the document is being uploaded
   * @param annotation Optional annotation for the document
   * @returns boolean indicating if the update was successful
   */
  public updateDocument(
    sectionId: string,
    documentId: string,
    status: DocumentStatus,
    fileName: string = '',
    annotation: string = ''
  ): boolean {
    if (!sectionId || !documentId) {
      CommonUtils.logWarn('updateDocument called with invalid sectionId or documentId', { sectionId, documentId });
      return false;
    }

    const documents = this.sectionDocumentMappings.get(sectionId);
    if (!documents) {
      CommonUtils.logWarn(`Section ${sectionId} not found`);
      return false;
    }

    const docIndex = documents.findIndex(doc => doc && doc.documentId === documentId);
    if (docIndex === -1) {
      CommonUtils.logWarn(`Document ${documentId} not found in section ${sectionId}`);
      return false;
    }

    const currentDoc = documents[docIndex];
    if (!currentDoc) {
      CommonUtils.logWarn(`Document at index ${docIndex} is undefined in section ${sectionId}`);
      return false;
    }

    const updatedDoc: DocumentItem = {
      ...currentDoc,
      status: status || currentDoc.status || DocumentStatus.NOT_STARTED,
      annotation: annotation !== undefined ? annotation : currentDoc.annotation,
      fileName: fileName || currentDoc.fileName,
      uploadDate: fileName ? new Date().toISOString() : currentDoc.uploadDate
    };

    const updatedDocuments = [...documents];
    updatedDocuments[docIndex] = updatedDoc;
    this.sectionDocumentMappings.set(sectionId, updatedDocuments);
    
    return true;
  }

  /**
   * Reinitialize mappings from filtered sections (respects Y/N conditions from CSV)
   * @param filteredSections The sections already filtered by loan purpose, exit strategy, etc.
   */
  public initializeFromFilteredSections(filteredSections: any[]): void {
    this.sectionDocumentMappings.clear();
    const addSectionDocuments = (section: any) => {
      if (section.id && Array.isArray(section.documents)) {
        this.sectionDocumentMappings.set(
          section.id,
          section.documents.map((doc: any) =>
            this.createDocumentItem(doc.id, doc.name, doc.required, doc.description)
          )
        );
      }
      // If this is a group, recurse into its sections
      if (Array.isArray(section.sections)) {
        section.sections.forEach(addSectionDocuments);
      }
    };
    filteredSections.forEach(addSectionDocuments);
  }

  /**
   * Get documents for a specific section
   * @param sectionId The ID of the section
   * @returns Array of document items for the section, or empty array if section doesn't exist
   */
  public getDocumentsForSection(sectionId: string): DocumentItem[] {
    if (!sectionId) {
      CommonUtils.logWarn('getDocumentsForSection called with undefined or null sectionId');
      return [];
    }
    
    const documents = this.sectionDocumentMappings.get(sectionId);
    if (!documents) {
      return [];
    }
    
    // Ensure all documents have a valid status
    return documents.map(doc => ({
      ...doc,
      status: doc.status || DocumentStatus.NOT_STARTED
    }));
  }

  /**
   * Get all section mappings with their document items
   * @returns Array of section mappings with their documents
   */
  public getAllSectionMappings(): SectionDocumentMapping[] {
    const mappings: SectionDocumentMapping[] = [];
    
    this.sectionDocumentMappings.forEach((documents, sectionId) => {
      mappings.push({
        sectionId,
        sectionTitle: this.getSectionTitle(sectionId),
        documents
      });
    });

    return mappings;
  }

  // Section title to be displayed in Documents tab
  // TODO-Sree-P2: Move this to ApplicationData.ts
  private getSectionTitle(sectionId: string): string {
    const titleMap: Record<string, string> = {
      // 2.2 PROPERTY DETAILS
      'upload_signed_purchase_contract': 'Signed Purchase Contract',
      'upload_signed_contract_assignment': 'Signed Contract Assignment',
      'upload_mortgage_statement': 'Mortgage Statement',
      'upload_property_comparables': 'Property Comparables',
      'upload_rent_roll_opex': 'Rent Roll & OpEx Schedule',
      'upload_in_place_leases': 'In-Place Leases',
      
      // 3.2 PROJECT DETAILS
      'upload_building_plans': 'Building Plans',
      'upload_active_building_permit': 'Building Permit',

      // 5.3 ENTITY DETAILS
      'upload_entity_formation_document': 'Entity Formation Document',
      'upload_irs_ein_letter': 'IRS EIN Letter',
      'upload_operating_agreement': 'Operating Agreement',
      'upload_certificate_of_good_standing': 'Certificate of Good Standing',

      // 5.3 ENTITY PARTICIPANT DETAILS
      'upload_government_issued_id': 'Government-Issued ID',
      'upload_proof_of_liquidity': 'Proof of Liquidity',

      // 6.3 MISCELLANEOUS
      'miscellaneous': 'Miscellaneous'
    };

    return titleMap[sectionId] || sectionId;
  }

  /**
   * Add or update document mapping for a section
   */
  public updateSectionDocuments(sectionId: string, documents: DocumentItem[]): void {
    this.sectionDocumentMappings.set(sectionId, documents);
  }

  /**
   * Type for new document data when adding to a section
   */
  public static NewDocumentData = class {
    name?: string;
    description?: string;
    documentId?: string;
    title?: string;
    fileName?: string;
    fileSize?: string;
    fileType?: string;
    uploadedIds?: string[];
  };
}

// Export singleton instance for easy access
export const documentMappingService = DocumentMappingService.getInstance();
